import React, { useMemo } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { TabProps } from "../types";
import useReportFilter from "../useReportFilter";
import { useMetrics } from "../useMetrics";
import useEcosystem from "../referrals/ecosystemApi";
import StatRow, { totalsFor } from "../referrals/StatRow";
import ReferralCharts from "../referrals/ReferralCharts";
import PipelineGrouping from "../referrals/PipelineGrouping";
import MetricsGrouping from "../referrals/MetricsGrouping";
import { EmptyState, MAGENTA_TINT, PRIMARY } from "../referrals/parts";
import { useViewport } from "../../../../utils/useViewport";

const GROUPINGS = [
  { value: "partner", label: "Partner" },
  { value: "category", label: "Category" },
  { value: "consultant", label: "Consultant" },
  { value: "month", label: "Month" },
];
const DEFAULT_GROUPING = "partner";

export type ReferralKind = "provider" | "resource";

/** Per-tab copy — the kind is fixed by the tab, not chosen on the page. */
const COPY: {
  [K in ReferralKind]: {
    title: string;
    subtitle: string;
    filenameKind: string;
    emptyTitle: string;
    partnerNoun: string;
  };
} = {
  resource: {
    title: "Resource Referrals",
    subtitle: "Every referral sent to a resource partner",
    filenameKind: "resource_referrals",
    emptyTitle: "No resource referrals found for this period",
    partnerNoun: "resource partners",
  },
  provider: {
    title: "B2B Referrals",
    subtitle: "Every referral sent to a B2B provider",
    filenameKind: "b2b_referrals",
    emptyTitle: "No B2B referrals found for this period",
    partnerNoun: "B2B providers",
  },
};

interface ReferralsTabProps extends Partial<TabProps> {
  /** Which half of the referral ecosystem this tab reports on. */
  kind: ReferralKind;
  /** URL-backed grouping: ?sub=partner|category|consultant|month */
  sub: string;
  setSub: (sub: string) => void;
}

/**
 * One referral report per partner kind — "Resource Referrals" and
 * "B2B Referrals" mount this same component with a fixed `kind`, replacing the
 * former unified tab and its All/B2B/Resource chips. The B2B / Resource Partner
 * / Ecosystem legacy reports stay deep-linkable at /dashboard/reports/:id.
 *
 * Partner and category groupings come from `GET /reports/ecosystem` (referrals
 * grouped by the partner they were sent to, with the persisted pipeline
 * funnel); consultant and month come from `GET /reports/metrics`. All four
 * respect the shared consultant filter.
 */
const ReferralsTab: React.FC<ReferralsTabProps> = (props) => {
  const { kind, sub, setSub } = props;
  const copy = COPY[kind];
  const { isPhone } = useViewport();
  // The layout currently mounts this tab with only { kind, sub, setSub }; fall
  // back to the URL-backed filter so the shared range/consultant still apply,
  // and prefer the props if the layout starts passing TabProps.
  const filter = useReportFilter();
  const start = props.start !== undefined ? props.start : filter.start;
  const end = props.end !== undefined ? props.end : filter.end;
  const consultant =
    props.consultant !== undefined ? props.consultant : filter.consultant;

  const grouping = GROUPINGS.some((g) => g.value === sub)
    ? sub
    : DEFAULT_GROUPING;
  const isPipelineGrouping = grouping === "partner" || grouping === "category";

  // One fetch per (range, consultant), shared by both referral tabs via the
  // module cache.
  const ecosystem = useEcosystem(start, end, consultant);
  const metrics = useMetrics({
    start,
    end,
    consultant,
    groupBy: grouping === "month" ? "month" : "consultant",
    enabled: !isPipelineGrouping,
  });

  const partners = useMemo(
    () => (ecosystem.data?.partners || []).filter((p) => p.kind === kind),
    [ecosystem.data, kind],
  );

  const totals = useMemo(() => totalsFor(partners), [partners]);

  const rangeLabel = filter.rangeLabel;
  // The filename records the active filters, so a consultant-scoped export is
  // never mistaken for the org-wide one.
  const filenamePrefix = `${copy.filenameKind}_${
    ecosystem.data?.range?.start || "all"
  }_${ecosystem.data?.range?.end || "today"}${
    consultant ? `_consultant_${consultant}` : ""
  }`;
  const noPartners = Boolean(ecosystem.data) && partners.length === 0;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c2c34" }}>
          {copy.title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
          {copy.subtitle} · {rangeLabel}
        </Typography>
      </Box>

      {ecosystem.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {ecosystem.error}
        </Alert>
      )}

      <StatRow
        totals={totals}
        loading={ecosystem.loading && !ecosystem.data}
        partnerNoun={copy.partnerNoun}
      />

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 3, mb: 2 }}
      >
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ color: "#8a8a93" }}>
            Group by
          </Typography>
          {/* Phones: the four groupings fit as one select. */}
          {isPhone ? (
            <Select
              size="small"
              value={grouping}
              onChange={(e) => setSub(String(e.target.value))}
              inputProps={{ "aria-label": "Group by" }}
            >
              {GROUPINGS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={grouping}
              onChange={(_, value: string | null) => value && setSub(value)}
              sx={{
                "& .Mui-selected": {
                  backgroundColor: `${MAGENTA_TINT} !important`,
                  color: PRIMARY,
                  fontWeight: 700,
                },
              }}
            >
              {GROUPINGS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Stack>
      </Stack>

      {ecosystem.loading && !ecosystem.data && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={24} sx={{ color: PRIMARY }} />
        </Stack>
      )}

      {isPipelineGrouping && ecosystem.data && partners.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ReferralCharts partners={partners} />
        </Box>
      )}

      {isPipelineGrouping &&
        ecosystem.data &&
        (noPartners ? (
          <EmptyState
            title={copy.emptyTitle}
            hint="Try widening the date range from the filter bar above."
          />
        ) : (
          <PipelineGrouping
            partners={partners}
            groupBy={grouping === "category" ? "category" : "partner"}
            filenamePrefix={filenamePrefix}
          />
        ))}

      {!isPipelineGrouping && (
        <MetricsGrouping
          data={metrics.data}
          loading={metrics.loading}
          error={metrics.error}
          kind={kind}
          mode={grouping === "month" ? "month" : "consultant"}
          filenamePrefix={filenamePrefix}
        />
      )}
    </Box>
  );
};

export default ReferralsTab;
